"use client";

import { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import {
  Search,
  Filter,
  X,
  FileText,
  Video,
  BookOpen,
  Link2,
  Star,
  Eye,
  Download,
  ExternalLink,
  Calendar,
  User,
  Tag,
  Loader2,
  AlertCircle,
  RefreshCw,
  Grid3X3,
  List,
  BookMarked,
  Briefcase,
  Sparkles,
  Film,
} from "lucide-react";
import api from "@/lib/axiosInstance";

interface Resource {
  _id: string;
  title: string;
  description: string;
  thumbnail: string;
  type: "devotional" | "pdf" | "video" | "article" | "link" | "other";
  category: string;
  author: string;
  tags: string[];
  featured: boolean;
  views?: number;
  downloads?: number;
  linkType?: string;
  createdAt: string;
}

// Resource type configuration
const resourceTypeConfig: Record<string, { icon: any; color: string; bgColor: string; label: string }> = {
  pdf: { icon: FileText, color: "text-red-500", bgColor: "bg-red-50", label: "PDF" },
  video: { icon: Video, color: "text-blue-500", bgColor: "bg-blue-50", label: "Video" },
  devotional: { icon: BookOpen, color: "text-purple-500", bgColor: "bg-purple-50", label: "Devotional" },
  article: { icon: FileText, color: "text-green-500", bgColor: "bg-green-50", label: "Article" },
  link: { icon: Link2, color: "text-orange-500", bgColor: "bg-orange-50", label: "External Link" },
  other: { icon: FileText, color: "text-gray-500", bgColor: "bg-gray-50", label: "Other" },
};

const categories = [
  { value: "all", label: "All Categories", icon: Grid3X3 },
  { value: "academic", label: "Academic", icon: BookMarked },
  { value: "spiritual", label: "Spiritual", icon: Sparkles },
  { value: "career", label: "Career", icon: Briefcase },
  { value: "media", label: "Media", icon: Film },
];

// Fetch resources hook
const useResources = () => {
  return useQuery({
    queryKey: ["public-resources"],
    queryFn: async () => {
      const response = await api.get("/resources");
      return response.data as Resource[];
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

// Resource Card Component
const ResourceCard = ({ resource }: { resource: Resource }) => {
  const typeConfig = resourceTypeConfig[resource.type] || resourceTypeConfig.other;
  const Icon = typeConfig.icon;

  return (
    <Link href={`/resources/${resource._id}`} className="group">
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl hover:border-primary-200 transition-all duration-300 h-full flex flex-col">
        {/* Thumbnail */}
        <div className="relative h-48 overflow-hidden">
          <img
            src={resource.thumbnail || "/placeholder-resource.jpg"}
            alt={resource.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          
          {/* Type Badge */}
          <div className={`absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 ${typeConfig.bgColor} rounded-full`}>
            <Icon className={`w-4 h-4 ${typeConfig.color}`} />
            <span className={`text-xs font-semibold ${typeConfig.color}`}>{typeConfig.label}</span>
          </div>
          
          {/* Featured Badge */}
          {resource.featured && (
            <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 bg-secondary-500 text-white rounded-full">
              <Star className="w-3 h-3 fill-current" />
              <span className="text-xs font-semibold">Featured</span>
            </div>
          )}

          {/* Category */}
          <div className="absolute bottom-4 left-4">
            <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-gray-700 rounded-full text-xs font-medium capitalize">
              {resource.category}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 flex-1 flex flex-col">
          <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {resource.title}
          </h3>
          <p className="text-gray-600 text-sm line-clamp-2 mb-4 flex-1">
            {resource.description}
          </p>

          {/* Meta Info */}
          <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              <span className="truncate max-w-[100px]">{resource.author}</span>
            </div>
            <div className="flex items-center gap-3">
              {resource.views !== undefined && (
                <div className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  <span>{resource.views}</span>
                </div>
              )}
            </div>
          </div>

          {/* Tags */}
          {resource.tags && resource.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {resource.tags.slice(0, 3).map((tag, idx) => (
                <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

// Loading Skeleton
const ResourceSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 animate-pulse">
    <div className="h-48 bg-gray-200" />
    <div className="p-5 space-y-3">
      <div className="h-5 bg-gray-200 rounded w-3/4" />
      <div className="h-4 bg-gray-200 rounded w-full" />
      <div className="h-4 bg-gray-200 rounded w-2/3" />
      <div className="h-4 bg-gray-200 rounded w-1/2 mt-4" />
    </div>
  </div>
);

export default function ResourcesPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const {
    data: resources = [],
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useResources();

  // Get unique types from resources
  const availableTypes = useMemo(() => {
    const types = new Set(resources.map((r) => r.type));
    return Array.from(types);
  }, [resources]);

  // Featured resources
  const featuredResources = useMemo(() => {
    return resources.filter((r) => r.featured).slice(0, 4);
  }, [resources]);

  // Filter resources
  const filteredResources = useMemo(() => {
    return resources.filter((resource) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (
          !resource.title.toLowerCase().includes(query) &&
          !resource.description?.toLowerCase().includes(query) &&
          !resource.author?.toLowerCase().includes(query) &&
          !resource.tags?.some((tag) => tag.toLowerCase().includes(query))
        ) {
          return false;
        }
      }
      if (selectedCategory !== "all" && resource.category !== selectedCategory) {
        return false;
      }
      if (selectedType !== "all" && resource.type !== selectedType) {
        return false;
      }
      return true;
    });
  }, [resources, searchQuery, selectedCategory, selectedType]);

  const clearFilters = useCallback(() => {
    setSearchQuery("");
    setSelectedCategory("all");
    setSelectedType("all");
  }, []);

  const hasActiveFilters = searchQuery || selectedCategory !== "all" || selectedType !== "all";

  // Statistics
  const stats = useMemo(() => ({
    total: resources.length,
    pdfs: resources.filter((r) => r.type === "pdf").length,
    videos: resources.filter((r) => r.type === "video").length,
    articles: resources.filter((r) => r.type === "article" || r.type === "devotional").length,
    links: resources.filter((r) => r.type === "link").length,
  }), [resources]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-accent-100 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-800 via-primary-700 to-primary-900 py-16 lg:py-24">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        {/* Animated Orbs */}
        <div className="absolute top-10 left-10 w-64 h-64 bg-secondary-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-10 right-20 w-80 h-80 bg-primary-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              <span>Knowledge Hub</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Learning <span className="text-secondary-400">Resources</span>
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-10">
              Access educational materials, spiritual growth tools, career resources, and more from our community.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex-1 flex items-center px-6 py-4">
                  <Search className="w-5 h-5 text-gray-400 mr-3" />
                  <input
                    type="text"
                    placeholder="Search resources, articles, videos..."
                    className="w-full text-gray-700 placeholder-gray-400 focus:outline-none text-lg"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery("")} className="p-1 hover:bg-gray-100 rounded-full">
                      <X className="w-5 h-5 text-gray-400" />
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                    showFilters || hasActiveFilters
                      ? "bg-primary-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <Filter className="w-5 h-5" />
                  Filters
                  {hasActiveFilters && <span className="w-2 h-2 bg-secondary-500 rounded-full" />}
                </button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-6 mt-12">
              {[
                { icon: FileText, value: stats.pdfs, label: "PDFs" },
                { icon: Video, value: stats.videos, label: "Videos" },
                { icon: BookOpen, value: stats.articles, label: "Articles" },
                { icon: Link2, value: stats.links, label: "Links" },
              ].map((stat, idx) => (
                <div key={idx} className="flex items-center gap-3 text-white">
                  <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                    <stat.icon className="w-6 h-6" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold">{stat.value}</div>
                    <div className="text-white/70 text-sm">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Filters Panel */}
      {showFilters && (
        <section className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Category Filter */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Category</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => {
                    const Icon = cat.icon;
                    return (
                      <button
                        key={cat.value}
                        onClick={() => setSelectedCategory(cat.value)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                          selectedCategory === cat.value
                            ? "bg-primary-600 text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {cat.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Type Filter */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Resource Type</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedType("all")}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      selectedType === "all"
                        ? "bg-primary-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    All Types
                  </button>
                  {availableTypes.map((type) => {
                    const config = resourceTypeConfig[type] || resourceTypeConfig.other;
                    const Icon = config.icon;
                    return (
                      <button
                        key={type}
                        onClick={() => setSelectedType(type)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                          selectedType === type
                            ? `${config.bgColor} ${config.color} ring-2 ring-offset-1`
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {config.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* View Mode & Clear */}
              <div className="flex flex-col justify-between">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">View</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`p-3 rounded-xl transition-all ${
                        viewMode === "grid" ? "bg-primary-100 text-primary-600" : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      <Grid3X3 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setViewMode("list")}
                      className={`p-3 rounded-xl transition-all ${
                        viewMode === "list" ? "bg-primary-100 text-primary-600" : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      <List className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="mt-4 text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Clear all filters
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Error State */}
        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-red-800">Error loading resources</h3>
                <p className="text-red-600 mt-1">
                  {(error as any)?.message || "Failed to load resources. Please try again."}
                </p>
                <button
                  onClick={() => refetch()}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Featured Resources */}
        {!hasActiveFilters && featuredResources.length > 0 && (
          <section className="mb-16">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Star className="w-6 h-6 text-secondary-500 fill-current" />
                  Featured Resources
                </h2>
                <p className="text-gray-600 mt-1">Top picks from our community</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredResources.map((resource) => (
                <ResourceCard key={resource._id} resource={resource} />
              ))}
            </div>
          </section>
        )}

        {/* All Resources */}
        <section>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {hasActiveFilters ? "Filtered Results" : "All Resources"}
              </h2>
              <p className="text-gray-600 mt-1">
                {filteredResources.length} resource{filteredResources.length !== 1 ? "s" : ""} available
              </p>
            </div>
            
            {isRefetching && (
              <div className="flex items-center gap-2 text-primary-600">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm font-medium">Refreshing...</span>
              </div>
            )}
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className={`grid gap-6 ${
              viewMode === "grid" ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" : "grid-cols-1"
            }`}>
              {[...Array(8)].map((_, i) => (
                <ResourceSkeleton key={i} />
              ))}
            </div>
          ) : filteredResources.length === 0 ? (
            /* Empty State */
            <div className="text-center py-20">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
                <BookOpen className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No resources found</h3>
              <p className="text-gray-600 max-w-md mx-auto mb-6">
                {resources.length === 0
                  ? "No resources have been published yet. Check back soon!"
                  : "Try adjusting your search or filter criteria to find what you're looking for."}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            /* Resources Grid */
            <div className={`grid gap-6 ${
              viewMode === "grid" ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" : "grid-cols-1"
            }`}>
              {filteredResources.map((resource) => (
                <ResourceCard key={resource._id} resource={resource} />
              ))}
            </div>
          )}
        </section>
      </main>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-primary-700 to-primary-900 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Have Resources to Share?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Contribute to our community by sharing your knowledge, articles, or educational materials.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              href="/auth"
              className="inline-flex items-center gap-2 px-8 py-4 bg-secondary-500 text-white rounded-xl font-semibold hover:bg-secondary-600 transition-colors shadow-lg"
            >
              <BookOpen className="w-5 h-5" />
              Start Contributing
            </Link>
            <Link
              href="/alumni"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 text-white rounded-xl font-semibold hover:bg-white/20 transition-colors backdrop-blur-sm"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
